import type { CaseOverviewOut, VolumeOut } from "@/api";

import { stageOf } from "./stages";

type BlockerCode = VolumeOut["blockerCodes"][number];

/**
 * Why a calculation is incomplete where the reason is not a price (S-11):
 * „Preis fehlt" beside a wall nobody measured sent sales to the price list
 * when the errand was the drawing. One word per reason, in the server's order.
 */
const BLOCKER_WORDS: Record<BlockerCode, string> = {
  no_gratings: "Roste fehlen",
  walls_without_measure: "Maß fehlt",
  measure_implausible: "Maß prüfen",
  plan_finding: "Planung prüfen",
  console_width_mismatch: "Konsole passt nicht",
  supplier_incomplete: "Lieferant unvollständig",
};

/**
 * The stages whose own word claims a release is the next step or a price is
 * what is missing. A blocking plan finding on a fully priced calculation
 * still lands in `calculation_ready` — „priced through", not „releasable" —
 * and „Freigabe offen" beside it promised a release the server refuses
 * (15.10.2026). Past a release a calculation carries no blocker.
 */
const BLOCKABLE: ReadonlySet<CaseOverviewOut["status"]> = new Set([
  "price_data_missing",
  "calculation_ready",
  "commercial_review",
]);

/**
 * The reasons as words — only where the stage's own word would be „Preis
 * fehlt" or „Freigabe offen" (`BLOCKABLE`). A code this bundle does not know
 * is left out rather than shown raw: the server may be newer than the browser.
 */
function blockerWords(item: CaseOverviewOut): string[] {
  if (!BLOCKABLE.has(item.status) || item.volume === null || item.volume.stale) return [];
  return item.volume.blockerCodes.filter((code) => Object.hasOwn(BLOCKER_WORDS, code)).map((code) => BLOCKER_WORDS[code]);
}

function gapWords(count: number): string {
  return count === 1 ? "1 Lücke" : `${count} Lücken`;
}

/**
 * The gaps, where the stage's words name them (UX gate round 4, #2), or
 * `null`:
 * - A release with gaps (D-24) under the chip „Freigegeben" says only the
 *   gaps, „mit 1 Lücke" — „Freigegeben · freigegeben mit 1 Lücke" said the
 *   same word twice. Under a later chip („Angebot raus", „Gewonnen") the
 *   whole of it, „freigegeben mit n Lücken", as before.
 * - A partial sum with gaps is not without a price: it has one, with „1
 *   Lücke offen" — „Preis fehlt" beside 994,28 € contradicted the amount
 *   (screen check S-11). „Preis fehlt" stays where there is no amount.
 */
function gapPhrase(item: CaseOverviewOut): string | null {
  const volume = item.volume;
  // A calculation of an older Stand (UX-Gutachten 23.10.2026, Aufgabe 1): its
  // release and its gaps speak for a plan that is no longer on the board.
  if (volume === null || volume.stale) return null;
  const gaps = gapWords(volume.gapCount);
  if (volume.released && volume.releasedWithGaps) {
    return item.status === "quote_approved" ? `mit ${gaps}` : `freigegeben mit ${gaps}`;
  }
  if (item.status === "price_data_missing" && volume.vkTotal !== null && volume.gapCount > 0) return `${gaps} offen`;
  return null;
}

/**
 * Whether the words under the chip already count the gaps — then the amount
 * beside them does not count them a second time (`CaseVolume`): one fact,
 * one place in a row.
 */
export function gapsInWords(item: CaseOverviewOut): boolean {
  return gapPhrase(item) !== null;
}

/**
 * The words beside a stage chip (design §8.1): the reasons a calculation
 * cannot be released that are not a price („Maß fehlt", `blockerWords`),
 * then the gaps where the calculation has them to say (`gapPhrase`) — or
 * else the stage's own note („Freigabe offen", „Preis fehlt"). „Preis fehlt"
 * beside a reason only where prices are missing too (S-11). In `lib/` because the register and the
 * start page's „Zuletzt bearbeitet" both show them (UX ruling 11: Chiara
 * sees on the start page what she is handing on). An open handover is not
 * one of them: `CaseStage` names its recipient after them (UX gate round 4,
 * #6), with a sentence of its own for the screen reader.
 */
export function extraWords(item: CaseOverviewOut): string[] {
  const words = blockerWords(item);
  const gaps = gapPhrase(item);
  // The stage's note only without a reason — or beside one where prices are
  // missing too. „Freigabe offen" beside „Planung prüfen" is the same false
  // promise the reason replaces.
  const noteApplies = words.length === 0 || (item.volume !== null && item.volume.gapCount > 0 && item.status === "price_data_missing");
  const note = gaps ?? (noteApplies ? stageOf(item.status).note : null);
  if (note !== null) words.push(note);
  return words;
}
